import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import AppContext from '@/contexts/appContext';
import OrganizationForm from '@/forms/OrganizationForm';
import { useDugoutOrganization } from '@/hooks/useDugoutOrganizations';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import NotAuthorized from '@/ui/components/NotAuthorized';
import Spinner from '@/ui/components/Spinner';
import { hasPermission, ORGANIZATION_CHANGE, ORGANIZATION_VIEW } from '@/utils/userPermission';

/**
 * Edit an organization. There's no create page: organizations come from the
 * Kustomer company sync, and one made here would collide with it.
 */
const OrganizationEdit = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { permissions } = useContext(AppContext);

  // Editing loads the org first, and that GET needs view as well as change --
  // Django model permissions don't imply one another.
  const isAllowed = hasPermission({
    permissions,
    requiredPermissions: [ORGANIZATION_VIEW, ORGANIZATION_CHANGE]
  });

  const { data, isPending } = useDugoutOrganization(isAllowed ? organizationId : undefined);
  const organization = data?.data;

  if (!isAllowed) return <NotAuthorized />;
  if (isPending) return <Spinner />;
  if (!organization) {
    return <NotAuthorized heading="Not found" message="No such organization." />;
  }

  const goBack = () => navigate('..', { relative: 'path' });

  return (
    <Container type="page">
      <PageHeader headingText={`Edit ${organization.name}`} />
      <Card className="mt-3">
        <OrganizationForm organization={organization} onCancel={goBack} onSuccess={goBack} />
      </Card>
    </Container>
  );
};

export default OrganizationEdit;
